import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Button } from '@coursera/coursera-ui';

import type Evaluation from 'bundles/code-evaluator/models/Evaluation';
import {
  addGraderConfig,
  clearAnswer,
  removeGraderConfig,
  setSelectedGraderConfig,
  submitForGrading,
  updateGraderConfig,
} from 'bundles/widget-simulator/actions/WidgetSimulatorActions';
import JsonViewerModal from 'bundles/widget-simulator/components/JsonViewerModal';
import GraderConfigurationEditorModal from 'bundles/widget-simulator/components/tools/GraderConfigurationEditorModal';
import OptionsFormGroup from 'bundles/widget-simulator/components/tools/OptionsFormGroup';
import ToolSection from 'bundles/widget-simulator/components/tools/ToolSection';
import type WidgetSimulatorStoreClass from 'bundles/widget-simulator/stores/WidgetSimulatorStore';
import type { GraderConfiguration } from 'bundles/widget-simulator/types';

import _t from 'i18n!nls/widget-simulator';

import 'css!./__styles__/Grading';

const styles = {
  buttonDisabled: {
    backgroundColor: 'var(--cds-color-blue-400)',
  },
};

// TODO: fix these types <-- seems the store initialized most of these as undefined?
export type Props = {
  options: Array<GraderConfiguration>;
  selectedOption: GraderConfiguration;
  answer: any;
  submittedAnswer: any;
  evaluation: Evaluation | undefined;
};

type State = {
  showViewAnswerModal: boolean;
  showViewEvaluationModal: boolean;
  showAddModal: boolean;
  showEditModal: boolean;
};

export class Grading extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof Grading.contextTypes>;

  state = {
    showViewAnswerModal: false,
    showViewEvaluationModal: false,
    showAddModal: false,
    showEditModal: false,
  };

  handleSubmitForGradingButtonClick = () => {
    const { executeAction } = this.context;
    executeAction(submitForGrading);
  };

  handleClearAnswerClick = () => {
    const { executeAction } = this.context;
    executeAction(clearAnswer);
  };

  handleViewAnswerClick = () => {
    this.setState({ showViewAnswerModal: true });
  };

  handleAnswerModalClose = () => {
    this.setState({ showViewAnswerModal: false });
  };

  handleViewEvaluationClick = () => {
    this.setState({ showViewEvaluationModal: true });
  };

  handleEvaluationModalClose = () => {
    this.setState({ showViewEvaluationModal: false });
  };

  handleAddButtonClick = () => {
    this.setState({ showAddModal: true });
  };

  handleEditButtonClick = () => {
    this.setState({ showEditModal: true });
  };

  handleRemoveButtonClick = () => {
    const { executeAction } = this.context;
    const { options, selectedOption } = this.props;
    const selectedIndex = options.findIndex((option) => option.id === selectedOption.id);

    // TODO: Create a modal for this.
    // eslint-disable-next-line no-alert,
    if (confirm('Are you sure you want to delete this grader configuration? This cannot be undone.')) {
      executeAction(removeGraderConfig, { index: selectedIndex });
    }
  };

  handleAddSaveButtonClick = (newProps: $TSFixMe) => {
    const { executeAction } = this.context;

    executeAction(addGraderConfig, newProps);
    this.setState({ showAddModal: false });
  };

  handleEditSaveButtonClick = (newProps: $TSFixMe) => {
    const { executeAction } = this.context;
    const { selectedOption } = this.props;

    executeAction(updateGraderConfig, {
      id: selectedOption.id,
      newProps,
    });
    this.setState({ showEditModal: false });
  };

  handleSelectChange = (event: $TSFixMe) => {
    const { executeAction } = this.context;
    const { options } = this.props;
    const selectedId = event.target.value;
    const selectedIndex = options.findIndex((option) => option.id === selectedId);

    executeAction(setSelectedGraderConfig, { selectedIndex });
  };

  closeAddModal = () => {
    this.setState({ showAddModal: false });
  };

  closeEditModal = () => {
    this.setState({ showEditModal: false });
  };

  renderOptions() {
    const { options } = this.props;

    if (!options) {
      return <option>Loading...</option>;
    } else if (options.length === 0) {
      return <option>Add a Grader Configuration...</option>;
    } else {
      return options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ));
    }
  }

  renderHelp() {
    return (
      <div>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                This simulates the process of answering a question and then submitting a quiz.
              `
            )}
          />
        </p>
        <p>
          <FormattedHTMLMessage
            message={_t(
              `
                When server-side grading is enabled, the simulator will use your plugin's <code>grader.js</code> file
                to grade the answer.
              `
            )}
          />
        </p>
      </div>
    );
  }

  render() {
    const { options, selectedOption, answer, submittedAnswer, evaluation } = this.props;
    const { showViewAnswerModal, showViewEvaluationModal, showAddModal, showEditModal } = this.state;
    const hasAnswer = !!answer;
    const buttonStyles = {
      ...(!hasAnswer && styles.buttonDisabled),
    };

    return (
      <ToolSection className="rc-Grading" name={_t('Grading')} help={this.renderHelp()}>
        <OptionsFormGroup
          label={_t('Config')}
          options={options}
          selectedOption={selectedOption}
          onAddButtonClick={this.handleAddButtonClick}
          onEditButtonClick={this.handleEditButtonClick}
          onRemoveButtonClick={this.handleRemoveButtonClick}
        >
          <select className="control" value={selectedOption && selectedOption.id} onChange={this.handleSelectChange}>
            {this.renderOptions()}
          </select>
        </OptionsFormGroup>

        <div className="horizontal-box">
          {submittedAnswer ? (
            <Button
              isThemeDark={false}
              type="primary"
              size="sm"
              label={_t('Clear Answer')}
              onClick={this.handleClearAnswerClick}
              data-test="clear-answer"
            />
          ) : (
            <Button
              isThemeDark={false}
              type="primary"
              size="sm"
              label={_t('Submit for Grading')}
              style={buttonStyles}
              onClick={this.handleSubmitForGradingButtonClick}
              disabled={!hasAnswer}
              data-test="submit-for-grading"
            />
          )}

          {(answer || submittedAnswer) && (
            <Button
              isThemeDark={false}
              type="noStyle"
              size="sm"
              label={_t('Show Answer')}
              onClick={this.handleViewAnswerClick}
              data-test="show-answer"
            />
          )}

          {evaluation && (
            <Button
              isThemeDark={false}
              type="noStyle"
              size="sm"
              label={_t('Show Evaluation')}
              onClick={this.handleViewEvaluationClick}
              data-test="show-evaluation"
            />
          )}
        </div>

        {showViewAnswerModal && (
          <JsonViewerModal
            name={_t('Grading Answer Modal')}
            title={submittedAnswer ? _t('Submitted Answer') : _t('Answer')}
            json={JSON.stringify(submittedAnswer || answer, null, 2)}
            onClose={this.handleAnswerModalClose}
          />
        )}

        {showViewEvaluationModal && (
          <JsonViewerModal
            name={_t('Grading Evaluation Modal')}
            title={_t('Evaluation')}
            json={JSON.stringify(evaluation, null, 2)}
            onClose={this.handleEvaluationModalClose}
          />
        )}

        {showAddModal && (
          <GraderConfigurationEditorModal
            name={_t('Add Grader Configuration Modal')}
            title={_t('Add Grader Configuration')}
            onClose={this.closeAddModal}
            onSaveButtonClick={this.handleAddSaveButtonClick}
          />
        )}

        {showEditModal && (
          <GraderConfigurationEditorModal
            name={_t('Edit Grader Configuration Modal')}
            title={_t('Edit Grader Configuration')}
            config={selectedOption}
            onClose={this.closeEditModal}
            onSaveButtonClick={this.handleEditSaveButtonClick}
          />
        )}
      </ToolSection>
    );
  }
}

type Stores = {
  WidgetSimulatorStore: typeof WidgetSimulatorStoreClass;
};

export default compose<Props, {}>(
  connectToStores<Props, {}, Stores>(['WidgetSimulatorStore'], ({ WidgetSimulatorStore }) => ({
    options: WidgetSimulatorStore.getGraderConfigs(),
    selectedOption: WidgetSimulatorStore.getSelectedGraderConfig(),
    answer: WidgetSimulatorStore.getAnswer(),
    submittedAnswer: WidgetSimulatorStore.getSubmittedAnswer(),
    evaluation: WidgetSimulatorStore.getEvaluation(),
  }))
)(Grading);
